// Admin payments API surface — invoices, invoice detail and refunds. M7c.
//
// Split out of `lib/billing.ts` (which keeps plans + prices) because the money
// surface is now big enough to own a file, and because `lib/billing.ts` still
// exports the deprecated `formatMinor` that `lib/money.ts` replaces.

import { api } from './api';
import type { Currency } from './money';

export type InvoiceStatus =
  | 'draft'
  | 'open'
  | 'paid'
  | 'void'
  | 'uncollectible'
  | 'refunded'
  | 'partially_refunded';

export type RefundStatus = 'pending' | 'succeeded' | 'failed';

export interface AdminInvoiceRow {
  id: string;
  subscriptionId: string;
  ownerType: 'family' | 'school';
  ownerLabel: string;
  amountMinor: number;
  currency: Currency;
  status: InvoiceStatus;
  issuedAt: string;
  paidAt: string | null;
  /** Sum of SUCCEEDED refunds. Derived server-side, never stored. */
  refundedMinor: number;
}

export interface Refund {
  id: string;
  invoiceId: string;
  amountMinor: number;
  currency: Currency;
  reason: string;
  status: RefundStatus;
  providerRef: string | null;
  createdAt: string;
  refundedAt: string | null;
}

export interface AdminInvoiceDetail {
  id: string;
  subscriptionId: string;
  amountMinor: number;
  currency: Currency;
  status: InvoiceStatus;
  subtotalMinor: number | null;
  discountMinor: number;
  couponCode: string | null;
  planId: string | null;
  planSlug: string | null;
  issuedAt: string;
  dueAt: string | null;
  paidAt: string | null;
  providerRef: string | null;
  refunds: Refund[];
  refundedMinor: number;
  /**
   * What may still be refunded RIGHT NOW — already net of any in-flight
   * (pending) refund, so the UI cannot offer an amount the server would reject.
   */
  refundableMinor: number;
}

export async function listInvoices(
  status?: string,
  signal?: AbortSignal,
): Promise<AdminInvoiceRow[]> {
  const q = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  const { invoices } = await api<{ invoices: AdminInvoiceRow[] }>(`/admin/payments${q}`, {
    signal,
  });
  return invoices;
}

export async function getInvoice(
  invoiceId: string,
  signal?: AbortSignal,
): Promise<{ invoice: AdminInvoiceDetail; owner: { type: string; label: string } }> {
  return api(`/admin/payments/${invoiceId}`, { signal });
}

export interface PaymentsSummary {
  /** Gross, INCLUDING invoices that were later refunded. */
  collectedByCurrency: Record<string, number>;
  refundedByCurrency: Record<string, number>;
  /** collected − refunded, per currency. Never summed across currencies. */
  netByCurrency: Record<string, number>;
  subscriptionsByStatus: Record<string, number>;
  totalInvoices: number;
}

export async function getPaymentsSummary(signal?: AbortSignal): Promise<PaymentsSummary> {
  return api<PaymentsSummary>('/admin/payments/summary', { signal });
}

/**
 * Issue a refund.
 *
 * `amountMinor` omitted means the full REMAINING refundable balance — not the
 * invoice total, which would over-refund a partially-refunded invoice.
 * `reason` is required: it is the only record of why money moved.
 */
export async function refundInvoice(
  invoiceId: string,
  input: { amountMinor?: number; reason: string },
): Promise<{ refund: Refund; invoiceStatus: InvoiceStatus }> {
  return api(`/admin/payments/${invoiceId}/refund`, { method: 'POST', body: input });
}
